/**
 * Background Jobs — what is running now, and what the poller does next.
 *
 * This replaced the Recent Activity feed on the dashboard. Activity answers "what
 * already happened", which is a question for after the fact; standing at the
 * dashboard the live questions are *what is executing right now* — the only thing
 * a Stop button can act on — and *what fires next*, which is what tells an operator
 * whether they have minutes to act or seconds. The full audit feed is not lost: it
 * is the same data the Background Run tab lists.
 *
 * Two design points worth stating:
 *
 *  · RETRYING is shown beside RUNNING. Nothing is executing during a retry wait,
 *    but the fire is not over — it is sleeping before its next attempt, and that is
 *    exactly the state an operator wants to be able to stop before it tries again.
 *
 *  · "Stop requested" is a distinct state from stopped. The request is written to
 *    the database and acted on by whichever instance holds the run, so there is a
 *    real gap between asking and the status becoming CANCELLED. Showing the row as
 *    already stopped would be a lie during that gap, and the operator would press
 *    the button again.
 *
 * Polling is faster here than on the tiles (10s): a screen whose whole purpose is
 * "what is happening now" is worth less the staler it gets.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import { Message } from 'sharedUi/Message';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { backgroundJobService } from '../../../services/background-job.service';
import { useToastApi } from '../../../hooks/use-toast-api';
import { useJobPermissions } from './use-job-permissions';
import type { BackgroundJobRun, BackgroundJobSchedule } from '../../../types/background-job.types';

/** How many upcoming fires to list. Beyond this it stops being "next". */
const UPCOMING = 8;

/** "in 4m" / "due now" / an absolute time when it is far out. */
function relative(iso?: string | null): string {
  if (!iso) return '—';
  const ms = Date.parse(iso) - Date.now();
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'due now';

  // Seconds under the hour, and minutes FLOORED rather than rounded. Rounding
  // read "in 5m" for something 4m 32s away, so the countdown started high and
  // then appeared to stall while it caught up with itself. "in under a minute"
  // is gone for the same reason — an exact second is more use on a screen whose
  // whole purpose is what happens next.
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `in ${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `in ${mins}m ${secs % 60}s`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `in ${hours}h ${mins % 60}m`;
  return formatTimestamp(iso);
}

/** How long a run has been going, which is the number that makes one look stuck. */
function elapsed(iso?: string | null): string {
  if (!iso) return '—';
  const secs = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (Number.isNaN(secs)) return '—';
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

const Empty = ({ icon, text }: { icon: string; text: string }) => (
  <div className="nn-empty">
    <i className={`pi ${icon}`} />
    {text}
  </div>
);

/** A ONCE schedule that has already fired (or whose moment has passed) is done —
 *  not "next", and not "paused". Its nextRunAt is cleared once the engine claims
 *  it; when the engine is off it simply sits in the past. Either way, hide it. */
const isCompletedOnce = (s: BackgroundJobSchedule): boolean =>
  s.scheduleType === 'ONCE' && (!s.nextRunAt || Date.parse(s.nextRunAt) <= Date.now());

const NowAndNext = () => {
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  const { canExecute, canReadRuns, canToggle } = useJobPermissions();
  const [stopping, setStopping] = useState<string | null>(null);
  const [pausing, setPausing] = useState<string | null>(null);
  const [rerunning, setRerunning] = useState<string | null>(null);

  /* A one-second heartbeat for the countdowns.
   *
   * `relative()` and `elapsed()` are computed during render from a timestamp, so
   * without something forcing a re-render they only change when a query refetches.
   * That is what produced "due now" on a schedule that had already run: the value
   * was computed once, counted down to zero in the reader's head, and then sat
   * there — stale by up to a whole refetch interval — while the job fired and
   * finished in 0.2s. The data was never wrong; the screen simply stopped
   * recomputing it. */
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  /* Live work. RUNNING and RETRYING are fetched separately because the endpoint
     filters on a single status — cheaper than pulling all history and filtering
     client-side, which would grow with the collection.

     `enabled: canReadRuns` is the important part, not a nicety. This screen is
     gated on BACKGROUND_JOB.READ but /runs requires the separate
     BACKGROUND_JOB_RUN.READ, so it renders for people who hold one and not the
     other. Without this the dashboard would fire two guaranteed 403s on every
     load for those users and show an error where an explanation belongs. */
  const liveQs = useQueries({
    queries: (['RUNNING', 'RETRYING'] as const).map((status) => ({
      queryKey: ['bg-runs', 'live', status],
      queryFn: () => backgroundJobService.listRuns({ status, page: 1, page_size: 50 }),
      enabled: canReadRuns,
      staleTime: 5_000,
      refetchInterval: 10_000,
    })),
  });
  const live = useMemo(
    () => liveQs.flatMap((q) => q.data?.items ?? [])
      .sort((a, b) => Date.parse(b.startedAt || '') - Date.parse(a.startedAt || '')),
    [liveQs],
  );
  const liveLoading = liveQs.some((q) => q.isLoading);

  /* Names for the rows. Runs carry ids; one cached list resolves every row rather
     than a request per row. */
  const jobsQ = useQuery({
    queryKey: ['bg-jobs', 'all-for-now-next'],
    queryFn: () => backgroundJobService.listJobs({ page: 1, page_size: 200 }),
    staleTime: 60_000,
  });
  const jobs = useMemo(() => jobsQ.data?.items ?? [], [jobsQ.data]);
  const jobName = (id: string) => jobs.find((j) => j._id === id)?.jobName || id;

  /* Upcoming fires. Schedules are only listable per job, so this fans out — the
     same pattern the Schedules tab uses — and the results are merged and sorted
     by nextRunAt, which is the order the poller will actually take them in. */
  const scheduleQs = useQueries({
    queries: jobs.map((j) => ({
      queryKey: ['bg-schedules', j._id],
      queryFn: () => backgroundJobService.listSchedules(j._id),
      staleTime: 10_000,
      // 60s was far too slow for a panel about imminent work: a job on a 2-minute
      // interval could fire and complete entirely between two refetches, so the
      // screen kept showing a fire time that had already passed.
      refetchInterval: 20_000,
    })),
  });
  const upcoming = useMemo(() => {
    const rows: (BackgroundJobSchedule & { jobLabel: string })[] = [];
    scheduleQs.forEach((q, i) => {
      for (const s of q.data ?? []) {
        // Only what will actually fire. A disabled schedule, one with no next run,
        // or one whose JOB is inactive is not "next" — it is nothing. The last of
        // those is the case that used to sit at "due now" indefinitely: the engine
        // skips an inactive job's schedules without advancing nextRunAt, so the
        // time stays in the past for ever and the row promised a fire that could
        // never come.
        if (s.isScheduleEnabled && s.nextRunAt && jobs[i]?.isActive !== false && !isCompletedOnce(s)) {
          rows.push({ ...s, jobLabel: jobs[i]?.jobName || jobs[i]?._id || '—' });
        }
      }
    });
    return rows
      .sort((a, b) => Date.parse(a.nextRunAt!) - Date.parse(b.nextRunAt!))
      .slice(0, UPCOMING);
  }, [scheduleQs, jobs]);

  /* When a row's fire time passes, our copy of it is probably out of date — the
     engine claims within one poll interval and advances nextRunAt, so a time still
     in the past means we are looking at a cached value, not a stuck schedule.
     Refetch, throttled, so a genuinely stuck one cannot turn this into a loop. */
  const lastOverdueRefetch = useRef(0);
  useEffect(() => {
    const overdue = upcoming.some((s) => Date.parse(s.nextRunAt!) <= Date.now());
    if (!overdue) return;
    if (Date.now() - lastOverdueRefetch.current < 10_000) return;
    lastOverdueRefetch.current = Date.now();
    qc.invalidateQueries({ queryKey: ['bg-schedules'] });
  });

  /* Paused schedules — the other half of "Up next". They are deliberately NOT in
     the list above, which answers "what fires next" and must not be padded with
     things that never will. But they need to be visible somewhere: a schedule
     silently not firing is the failure mode this whole screen exists to catch. */
  const paused = useMemo(() => {
    const rows: (BackgroundJobSchedule & { jobLabel: string; reason: string })[] = [];
    scheduleQs.forEach((q, i) => {
      for (const s of q.data ?? []) {
        // Two different reasons a schedule will not fire, and the distinction is
        // what the operator needs: Resume fixes the first and does nothing for the
        // second, where the JOB itself has been deactivated.
        const reason = !s.isScheduleEnabled
          ? 'schedule paused'
          : jobs[i]?.isActive === false ? 'job inactive' : '';
        // A completed one-off is disabled by the engine, which would surface it
        // here as "schedule paused" — but it is finished, not paused. Hide it.
        if (reason && !isCompletedOnce(s)) {
          rows.push({ ...s, jobLabel: jobs[i]?.jobName || jobs[i]?._id || '—', reason });
        }
      }
    });
    return rows;
  }, [scheduleQs, jobs]);

  /* Stopping something that has NOT started is a different act from stopping a
     run, and needs a different control. A queued fire has no run document — no
     runId, nothing for Stop to address — so the only way to stop it is to prevent
     it: disable the schedule. The engine honours that even for work already queued
     inside a cycle, re-reading the schedule inside its concurrency slot.
     Deliberately "Pause" and not "Stop": it suspends every future fire, not one. */
  const pauseMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      backgroundJobService.toggleSchedule(scheduleId, false),
    onSuccess: () => {
      ok('Schedule paused. It will not fire until it is enabled again.');
      qc.invalidateQueries({ queryKey: ['bg-schedules'] });
      qc.invalidateQueries({ queryKey: ['bg-dashboard'] });
    },
    onError: fail,
    onSettled: () => setPausing(null),
  });

  const confirmPause = (scheduleId: string, label: string) => {
    confirmDialog({
      header: `Pause ${label}?`,
      icon: 'pi pi-pause-circle',
      // The consequence people get wrong is "every future fire", not "the next
      // one". That is the whole message; the rest was explaining the feature to
      // someone already using it.
      message: 'Suspends all future fires until re-enabled.',
      style: { width: '24rem' },
      acceptLabel: 'Pause',
      rejectLabel: 'Cancel',
      accept: () => {
        setPausing(scheduleId);
        pauseMutation.mutate(scheduleId);
      },
    });
  };

  /* Undoing a pause, from the same screen that did it.
     Without this, Pause is a one-way door: the row is filtered out of "Up next"
     the moment it stops being upcoming, and the only way back is another tab. A
     control whose effect cannot be reversed where it was applied is a trap. */
  const resumeMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      backgroundJobService.toggleSchedule(scheduleId, true),
    onSuccess: () => {
      ok('Schedule resumed.');
      qc.invalidateQueries({ queryKey: ['bg-schedules'] });
      qc.invalidateQueries({ queryKey: ['bg-dashboard'] });
    },
    onError: fail,
    onSettled: () => setPausing(null),
  });

  /* Recent failures. The tiles count them — "Failed today: 4" — and until now
     showed none of them, which is a number that raises a question the screen
     cannot answer. Five is enough to see a pattern without becoming a log. */
  const failuresQ = useQuery({
    queryKey: ['bg-runs', 'recent-failures'],
    queryFn: () => backgroundJobService.listRuns({
      status: 'FAILED', page: 1, page_size: 5, sort_by: 'startedAt', sort_order: 'desc',
    }),
    enabled: canReadRuns,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const rerunMutation = useMutation({
    mutationFn: (jobId: string) => backgroundJobService.runNow(jobId),
    onSuccess: () => {
      ok('Started. It appears under Running now.');
      qc.invalidateQueries({ queryKey: ['bg-runs'] });
      qc.invalidateQueries({ queryKey: ['bg-dashboard'] });
    },
    onError: fail,
    onSettled: () => setRerunning(null),
  });

  const stopMutation = useMutation({
    mutationFn: ({ runId, reason }: { runId: string; reason?: string }) =>
      backgroundJobService.stopRun(runId, reason),
    onSuccess: (res) => {
      // `stopRequested: false` is not a failure — the run finished between the
      // list being drawn and the button being pressed. Saying "stopped" there
      // would claim credit for something that did not happen.
      ok(res.stopRequested
        ? 'Stop requested. The run ends at its next checkpoint.'
        : 'That run had already finished — nothing to stop.');
      qc.invalidateQueries({ queryKey: ['bg-runs'] });
      qc.invalidateQueries({ queryKey: ['bg-dashboard'] });
    },
    onError: fail,
    onSettled: () => setStopping(null),
  });

  const confirmStop = (run: BackgroundJobRun) => {
    confirmDialog({
      header: `Stop ${jobName(run.jobId)}?`,
      icon: 'pi pi-exclamation-triangle',
      // Kept: "not rolled back" is the one thing that surprises people and cannot
      // be undone. Everything else that was here they can read on the screen.
      message: 'Halts remaining work and cancels queued retries. Work already done is not rolled back.',
      style: { width: '24rem' },
      acceptLabel: 'Stop',
      rejectLabel: 'Cancel',
      acceptClassName: 'p-button-danger',
      accept: () => {
        setStopping(run._id);
        stopMutation.mutate({ runId: run._id, reason: 'Stopped from the dashboard' });
      },
    });
  };

  return (
    <div className="nn-grid" data-testid="NowAndNext">
      {toast}
      <ConfirmDialog />

      {/* ── running now ── */}
      <section className="ea-card pad">
        <div className="ea-card-head">
          <div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#3B82F6,#6366F1)' }}><i className="pi pi-spin pi-spinner" style={{ color: '#fff', fontSize: 12 }} /></span>Running now</div>
          <span className="ea-sub">{live.length > 0 ? `${live.length} in flight` : 'live and retrying work'}</span>
        </div>

        {!canReadRuns ? (
          <Empty icon="pi-lock" text="Run visibility needs the Background Job Run read permission." />
        ) : liveLoading && live.length === 0 ? (
          <Empty icon="pi-spinner pi-spin" text="Loading…" />
        ) : live.length === 0 ? (
          <Empty icon="pi-check-circle" text="Nothing is running." />
        ) : (
          <ul className="nn-list nn-scroll">
            {live.map((run) => {
              const asked = !!run.cancelRequested;
              return (
                <li key={run._id} className="nn-row">
                  <div className="nn-main">
                    <p className="nn-t">{jobName(run.jobId)}</p>
                    <p className="nn-s">
                      {run.status === 'RETRYING' ? 'waiting to retry' : 'running'}
                      {` · attempt ${run.attemptNumber} · ${elapsed(run.startedAt)}`}
                      {run.triggeredBy === 'MANUAL_USER' ? ' · manual' : ''}
                    </p>
                  </div>
                  <div className="nn-actions">
                    {asked ? (
                      <Tag severity="warning" value="stop requested" />
                    ) : (
                      <Tag severity={run.status === 'RETRYING' ? 'warning' : 'info'} value={run.status.toLowerCase()} />
                    )}
                    <Button aria-label={!canExecute ? 'Needs the Background Job execute permission' : asked ? 'Already asked to stop; waiting for it to end' : undefined} label="Stop" icon="pi pi-stop-circle" size="small" outlined severity="danger"
                      data-testid={`stop-run-${run._id}`} loading={stopping === run._id}
                      disabled={!canExecute || asked || stopping === run._id}
                      tooltip={!canExecute ? 'Needs the Background Job execute permission' : asked ? 'Already asked to stop; waiting for it to end' : undefined}
                      tooltipOptions={{ showOnDisabled: true } as never} onClick={() => confirmStop(run)} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ── what the poller does next ── */}
      <section className="ea-card pad">
        <div className="ea-card-head">
          <div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#14B8A6,#6366F1)' }}><i className="pi pi-forward" style={{ color: '#fff', fontSize: 12 }} /></span>Up next</div>
          <span className="ea-sub">not started yet — pause to prevent the fire</span>
        </div>

        {upcoming.length === 0 ? (
          <Empty icon="pi-calendar-times" text="No enabled schedule has a next run time." />
        ) : (
          <ul className="nn-list nn-scroll">
            {upcoming.map((s) => (
              <li key={s._id} className="nn-row">
                <div className="nn-main">
                  <p className="nn-t">{s.jobLabel}</p>
                  <p className="nn-s">
                    {s.scheduleName || 'default'} · {s.scheduleType?.toLowerCase()}
                    {s.nextRunAt ? ` · ${formatTimestamp(s.nextRunAt)}` : ''}
                  </p>
                </div>
                <div className="nn-actions">
                  <span className="nn-when">{relative(s.nextRunAt)}</span>
                  <Button aria-label={canToggle ? 'Prevent this fire' : 'Needs schedule toggle permission'} label="Pause" icon="pi pi-pause" size="small" text
                    data-testid={`pause-schedule-${s._id}`} loading={pausing === s._id}
                    disabled={!canToggle || pausing === s._id}
                    tooltip={canToggle ? 'Prevent this fire' : 'Needs schedule toggle permission'}
                    tooltipOptions={{ showOnDisabled: true } as never} onClick={() => confirmPause(s._id, s.jobLabel)} />
                </div>
              </li>
            ))}
          </ul>
        )}

        {paused.length > 0 && (
          <div className="nn-divider">
            <p className="nn-sub-h">Paused — will not fire</p>
            <ul className="nn-list nn-scroll" style={{ maxHeight: 180 }}>
              {paused.map((s) => (
                <li key={s._id} className="nn-row">
                  <span className="nn-t" style={{ fontWeight: 500, color: 'var(--ea-ink-3)', fontSize: 12 }}>
                    {s.jobLabel} · {s.scheduleName || 'default'}
                    <span style={{ marginLeft: 4, color: 'var(--ea-ink-4)' }}>({s.reason})</span>
                  </span>
                  {s.reason === 'schedule paused' ? (
                    <Button aria-label={canToggle ? undefined : 'Needs the Background Job Schedule toggle permission'} label="Resume" icon="pi pi-play" size="small" text
                      data-testid={`resume-schedule-${s._id}`} loading={pausing === s._id}
                      disabled={!canToggle || pausing === s._id}
                      tooltip={canToggle ? undefined : 'Needs the Background Job Schedule toggle permission'}
                      tooltipOptions={{ showOnDisabled: true } as never}
                      onClick={() => { setPausing(s._id); resumeMutation.mutate(s._id); }} />
                  ) : (
                    <span className="nn-when" style={{ color: 'var(--ea-ink-4)' }}>reactivate the job</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="nn-foot">These fire only while the engine is running — see Settings for its health.</p>
      </section>

      {/* ── what went wrong ── */}
      <section className="ea-card pad" style={{ gridColumn: '1 / -1' }}>
        <div className="ea-card-head">
          <div className="ea-title"><span className="i" style={{ background: 'linear-gradient(135deg,#EF4444,#F59E0B)' }}><i className="pi pi-exclamation-triangle" style={{ color: '#fff', fontSize: 12 }} /></span>Recent failures</div>
          <span className="ea-sub">newest first — the cards count these, this is which ones</span>
        </div>

        {!canReadRuns ? (
          <Empty icon="pi-lock" text="Needs the Background Job Run read permission." />
        ) : (failuresQ.data?.items ?? []).length === 0 ? (
          <Empty icon="pi-check-circle" text="No failures recorded." />
        ) : (
          <ul className="nn-list nn-scroll">
            {(failuresQ.data?.items ?? []).map((run) => (
              <li key={run._id} className="nn-row">
                <div className="nn-main">
                  <p className="nn-t">{jobName(run.jobId)}</p>
                  <p className="nn-s">
                    {formatTimestamp(run.startedAt || undefined)}
                    {run.attemptNumber > 1 ? ` · after ${run.attemptNumber} attempts` : ''}
                    {run.logSummary ? ` · ${run.logSummary}` : ''}
                  </p>
                </div>
                <Button aria-label={canExecute ? undefined : 'Needs the Background Job execute permission'} label="Run again" icon="pi pi-replay" size="small" text
                  data-testid={`rerun-job-${run.jobId}`} loading={rerunning === run.jobId}
                  disabled={!canExecute || rerunning === run.jobId}
                  tooltip={canExecute ? undefined : 'Needs the Background Job execute permission'}
                  tooltipOptions={{ showOnDisabled: true } as never}
                  onClick={() => { setRerunning(run.jobId); rerunMutation.mutate(run.jobId); }} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {jobsQ.isError && (
        <Message className="w-full justify-start" style={{ gridColumn: '1 / -1' }} severity="warn"
          text="Job names could not be loaded, so rows show ids. The lists themselves are unaffected." />
      )}
    </div>
  );
};

export default NowAndNext;
